import React from "react";
import { FiEdit3, FiSave, FiTrash2, FiX } from "react-icons/fi";
import PublishProductionButton from "../PublishProductionButton";
import UnpublishProductionButton from "../UnpublishProductionButton";
import { LoadingSpinner } from "../LoadingSpinner";

export type EditorCommitActionsProps = {
	isEditing: boolean;
	onStartEdit?: () => void;
	editDisabled?: boolean;
	onCancelEdit?: () => void;
	onSave?: () => void;
	saveDisabled?: boolean;
	saving?: boolean;

	onDelete?: () => void;
	deleteDisabled?: boolean;
	deleting?: boolean;

	/** Stable content id for the publish / unpublish endpoints. */
	contentId?: string | null;
	publishDisabled?: boolean;
	/** Publishing deploys the last SAVED version, so it is meaningless while edits are pending. */
	publishHidden?: boolean;
	isProductionDeployed?: boolean;
	onPublished?: () => void;
	onPublishingChange?: (working: boolean) => void;
};

/**
 * Scales with the viewport via clamp() rather than stepping at breakpoints, so the top bar keeps to one
 * line. Arbitrary values on purpose: they compile to unique class names, which the second Tailwind
 * build (the renderer stylesheet) cannot clobber : a plain utility from that later sheet beats an
 * earlier responsive variant, the bug that silently killed xl:grid-cols-2 on /account.
 */
const BTN = "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-[clamp(0.5rem,0.85vw,0.875rem)] py-[clamp(0.25rem,0.45vw,0.375rem)] text-[clamp(0.7rem,0.85vw,0.875rem)] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50";
const NEUTRAL = `${BTN} border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50`;
/** Tighter than the bar below (fewer buttons up here), but the same rule: icons survive, words go. */
const LABEL = "max-[860px]:hidden";

/**
 * The commit actions : Publier / Dépublier / Supprimer / Enregistrer / Annuler / Modifier : fed to
 * `usePageHeader({ actions })` so they live in the app's TOP bar, hard right, on the page editor and
 * the content-data editor alike. Up there they never move: the same buttons in the same corner at every
 * width, whatever else the screen is doing. Everything secondary lives one level down in
 * {@link EditorActionBar}.
 *
 * Bordered, never filled.
 */
const EditorCommitActions: React.FC<EditorCommitActionsProps> = ({
	isEditing,
	onStartEdit,
	editDisabled,
	onCancelEdit,
	onSave,
	saveDisabled,
	saving,
	onDelete,
	deleteDisabled,
	deleting,
	contentId,
	publishDisabled,
	publishHidden,
	isProductionDeployed,
	onPublished,
	onPublishingChange
}) => (
	<div className="flex flex-nowrap items-center gap-2">
		{/* Not while editing: publishing deploys the last SAVED version, so offering it mid-edit either
		    lies about what goes live or invites you to ship the version you are busy replacing. Finish
		    the session (Enregistrer / Annuler) and it comes back. */}
		{!isEditing &&
			(publishHidden ? (
				<span className="inline-flex items-center whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-400">
					Enregistrez pour publier
				</span>
			) : (
				<PublishProductionButton
					contentId={contentId}
					disabled={publishDisabled}
					label={<span className={LABEL}>Publier</span>}
					onPublished={onPublished}
					onPublishingChange={onPublishingChange}
					className={`${BTN} border-[#005CA9]/40 bg-[#005CA9]/10 text-[#005CA9] hover:border-[#005CA9]/60 hover:bg-[#005CA9]/15`}
				/>
			))}

		{/* Destructive actions belong to an edit session : you open the editor, THEN you can take the
		    page down or delete it. And live in production ⇒ « Dépublier » stands in for « Supprimer »:
		    the server refuses to delete live content anyway (409), so unpublish is the way out and the
		    delete reappears once it is offline. */}
		{isEditing &&
			(isProductionDeployed ? (
				<UnpublishProductionButton
					contentId={contentId}
					label={<span className={LABEL}>Dépublier</span>}
					onUnpublished={onPublished}
					onWorkingChange={onPublishingChange}
					className={`${BTN} border-rose-200 bg-white text-rose-600 hover:bg-rose-50`}
				/>
			) : (
				onDelete && (
					<button
						type="button"
						onClick={onDelete}
						disabled={deleteDisabled || deleting}
						title="Supprimer"
						className={`${BTN} whitespace-nowrap border-rose-200 bg-white text-rose-700 hover:border-rose-300 hover:bg-rose-50`}
					>
						{deleting ? <LoadingSpinner size="sm" inline /> : <FiTrash2 className="h-3.5 w-3.5 shrink-0" />}
						<span className={LABEL}>{deleting ? "Suppression…" : "Supprimer"}</span>
					</button>
				)
			))}

		{/* Always last: the control that switches you in and out of editing. */}
		{isEditing ? (
			<>
				<button
					type="button"
					onClick={onSave}
					disabled={saveDisabled}
					title="Enregistrer"
					className={`${BTN} whitespace-nowrap border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100`}
				>
					{saving ? <LoadingSpinner size="sm" inline /> : <FiSave className="h-3.5 w-3.5 shrink-0" />}
					<span className={LABEL}>{saving ? "Enregistrement…" : "Enregistrer"}</span>
				</button>
				<button type="button" onClick={onCancelEdit} title="Annuler" className={NEUTRAL}>
					<FiX className="h-3.5 w-3.5 shrink-0" /> <span className={LABEL}>Annuler</span>
				</button>
			</>
		) : (
			onStartEdit && (
				<button type="button" onClick={onStartEdit} disabled={editDisabled} title="Modifier" className={NEUTRAL}>
					<FiEdit3 className="h-3.5 w-3.5 shrink-0" /> <span className={LABEL}>Modifier</span>
				</button>
			)
		)}
	</div>
);

export default EditorCommitActions;
